import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import { getReferralsByUsername, getAllUsersFromStorage, siteKey } from '../../../../utils/sharedStorage';

const COUNTRY_CONFIG: Record<string, { currency: string; activationFee: number }> = {
  'CI': { currency: 'FCFA', activationFee: 3500 },
  'SN': { currency: 'FCFA', activationFee: 3500 },
  'BJ': { currency: 'FCFA', activationFee: 3500 },
  'ML': { currency: 'FCFA', activationFee: 3500 },
  'TG': { currency: 'FCFA', activationFee: 3500 },
  'BF': { currency: 'FCFA', activationFee: 3500 },
  'GA': { currency: 'FCFA', activationFee: 3500 },
  'CM': { currency: 'FCFA', activationFee: 3500 },
  'CG': { currency: 'FCFA', activationFee: 3500 },
  'CD': { currency: 'FC',   activationFee: 9800 },
  'GH': { currency: 'GH₵', activationFee: 32   },
};

const REFERRAL_COMMISSIONS = { level1: 1500, level2: 600, level3: 400 };

interface Expense {
  id: string;
  label: string;
  amount: number;
  date: string;
  type: 'activation' | 'autre';
}

interface Profit {
  id: string;
  label: string;
  amount: number;
  date: string;
  type: 'parrainage' | 'tache' | 'roue' | 'autre';
  level?: number;
}

function lsGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch { return fallback; }
}

export default function Revenus() {
  const [currency, setCurrency] = useState('FCFA');
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [profits, setProfits] = useState<Profit[]>([]);
  const [activeTab, setActiveTab] = useState<'depenses' | 'profits'>('profits');
  const [isLoading, setIsLoading] = useState(true);
  const [realBalance, setRealBalance] = useState(0);
  const [commissionStats, setCommissionStats] = useState({ level1: 0, level2: 0, level3: 0, total: 0 });

  useEffect(() => {
    loadRevenueData();

    // Abonnement Supabase Realtime pour les mises à jour de solde
    if (supabase) {
      const user = localStorage.getItem('currentUser');
      if (user) {
        const parsedUser = JSON.parse(user);
        const userPhone = parsedUser.phone || '';
        
        if (userPhone) {
          const channel = supabase
            .channel('balance_updates')
            .on(
              'postgres_changes',
              {
                event: 'UPDATE',
                schema: 'public',
                table: 'users',
                filter: `phone=eq.${userPhone}`,
              },
              (payload) => {
                console.log('Mise à jour solde détectée:', payload);
                loadRevenueData(true);
              }
            )
            .subscribe();

          return () => {
            supabase.removeChannel(channel);
          };
        }
      }
    }

    // Fallback BroadcastChannel pour localStorage
    let balanceChannel: BroadcastChannel | null = null;
    try {
      balanceChannel = new BroadcastChannel('balance_update');
      balanceChannel.onmessage = (event) => {
        const raw = localStorage.getItem('currentUser');
        if (!raw) return;
        try {
          const cu = JSON.parse(raw);
          if (event.data?.phone === cu.phone) loadRevenueData(true);
        } catch { /* ignore */ }
      };
    } catch { /* ignore */ }

    const interval = setInterval(() => loadRevenueData(true), 20000);

    return () => {
      try { balanceChannel?.close(); } catch { /* ignore */ }
      clearInterval(interval);
    };
  }, []);

  /**
   * Cherche les filleuls d'un utilisateur depuis Supabase en priorité
   */
  const getFilleulsFor = async (identifier: string, name?: string) => {
    if (!identifier && !name) return [];
    const results = new Map<string, any>();

    // Chercher dans Supabase en priorité
    if (supabase && identifier) {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('site_id', 'SUCCESSLINK_AGENCIA')
          .or(`referred_by.eq.${identifier},referred_by.ilike.${identifier}`);

        if (!error && data) {
          data.forEach((u: any) => {
            results.set(u.phone, {
              phone: u.phone,
              name: u.name,
              username: u.username,
              referralCode: u.referral_code,
              referredBy: u.referred_by,
              isActivated: u.is_activated,
              activated: u.is_activated,
              accountStatus: u.is_activated ? 'active' : 'pending',
              activatedAt: u.activated_at,
              registrationDate: u.created_at,
              country: u.country,
            });
          });
        }
      } catch (err) {
        console.error('Erreur recherche filleuls Supabase:', err);
      }
    }

    // Fallback localStorage
    if (identifier) {
      const r1 = await getReferralsByUsername(identifier);
      r1.forEach((u: any) => {
        if (!results.has(u.phone)) results.set(u.phone, u);
      });
    }

    if (name) {
      const nameLower = name.toLowerCase().replace(/\s+/g, '');
      const allUsers = getAllUsersFromStorage();
      allUsers.forEach(u => {
        if (!u.referredBy) return;
        const ref = String(u.referredBy).toLowerCase().replace(/\s+/g, '');
        if (ref === nameLower && !results.has(u.phone)) results.set(u.phone, u);
      });
    }

    return Array.from(results.values());
  };

  const loadRevenueData = async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const user = localStorage.getItem('currentUser');
      if (!user) { setIsLoading(false); return; }

      const parsedUser = JSON.parse(user);
      const countryCode = parsedUser.country || 'CI';
      const config = COUNTRY_CONFIG[countryCode] || COUNTRY_CONFIG['CI'];
      setCurrency(config.currency);

      // Charger le solde depuis Supabase en priorité
      const userPhone = parsedUser.phone || '';
      let balance = Number(parsedUser.balance) || 0;
      
      if (supabase && userPhone) {
        try {
          const { data, error } = await supabase
            .from('users')
            .select('balance, is_activated, activated_at')
            .eq('site_id', 'SUCCESSLINK_AGENCIA')
            .eq('phone', userPhone)
            .maybeSingle();

          if (!error && data) {
            balance = Number(data.balance) || 0;
            
            // Synchroniser avec localStorage
            try {
              const cu = JSON.parse(localStorage.getItem('currentUser') || '{}');
              cu.balance = balance;
              cu.isActivated = data.is_activated;
              cu.activatedAt = data.activated_at;
              localStorage.setItem('currentUser', JSON.stringify(cu));
              localStorage.setItem(siteKey(`user_${userPhone}`), JSON.stringify(cu));
            } catch { /* ignore */ }
          }
        } catch (err) {
          console.error('Erreur chargement solde Supabase:', err);
        }
      }
      
      // Fallback localStorage si Supabase échoue
      if (!supabase || balance === 0) {
        const freshUser =
          lsGet<any>(siteKey(`user_${userPhone}`), null) ||
          lsGet<any>(`user_${userPhone}`, null);
        if (freshUser && typeof freshUser.balance === 'number') {
          balance = freshUser.balance;
          try {
            const cu = JSON.parse(localStorage.getItem('currentUser') || '{}');
            cu.balance = balance;
            localStorage.setItem('currentUser', JSON.stringify(cu));
          } catch { /* ignore */ }
        }
      }
      
      setRealBalance(balance);

      // --- DÉPENSES ---
      const savedExpenses: Expense[] = lsGet<Expense[]>('userExpenses', []);
      const isActivated = parsedUser.isActivated === true || parsedUser.activated === true || parsedUser.accountStatus === 'active';
      const alreadyHasActivation = savedExpenses.some(e => e.type === 'activation');
      if (isActivated && !alreadyHasActivation) {
        const activationExpense: Expense = {
          id: 'activation-fee',
          label: "Frais d'activation du compte",
          amount: config.activationFee,
          date: parsedUser.activationDate || new Date().toLocaleDateString('fr-FR'),
          type: 'activation',
        };
        savedExpenses.unshift(activationExpense);
        localStorage.setItem('userExpenses', JSON.stringify(savedExpenses));
      }
      setExpenses(savedExpenses);

      // --- PROFITS DE PARRAINAGE ---
      const myUsername = parsedUser.username || parsedUser.name || '';
      const myReferralCode = parsedUser.referralCode || myUsername;
      const myName = parsedUser.name || '';
      const referralProfits: Profit[] = [];
      let l1Total = 0, l2Total = 0, l3Total = 0;

      if (myUsername || myReferralCode) {
        // Niveau 1
        const level1Users = await getFilleulsFor(myUsername, myName);
        // Dédupliquer avec referralCode si différent
        if (myReferralCode && myReferralCode !== myUsername) {
          const extra = await getFilleulsFor(myReferralCode);
          extra.forEach((u: any) => {
            if (!level1Users.find((f: any) => f.phone === u.phone)) level1Users.push(u);
          });
        }

        for (const filleul of level1Users) {
          const filleulActivated = filleul.isActivated || filleul.activated === true || filleul.accountStatus === 'active';
          if (filleulActivated) {
            l1Total += REFERRAL_COMMISSIONS.level1;
            referralProfits.push({
              id: `ref-l1-${filleul.username || filleul.phone}`,
              label: `Parrainage Niveau 1 — ${filleul.name}`,
              amount: REFERRAL_COMMISSIONS.level1,
              date: filleul.activatedAt
                ? new Date(filleul.activatedAt).toLocaleDateString('fr-FR')
                : filleul.registrationDate
                  ? new Date(filleul.registrationDate).toLocaleDateString('fr-FR')
                  : new Date().toLocaleDateString('fr-FR'),
              type: 'parrainage',
              level: 1,
            });
          }

          // Niveau 2
          const filleulId = filleul.username || filleul.referralCode || filleul.name;
          if (filleulId) {
            const level2Users = await getFilleulsFor(filleulId, filleul.name);
            for (const level2 of level2Users) {
              const l2Activated = level2.isActivated || level2.activated === true || level2.accountStatus === 'active';
              if (l2Activated) {
                l2Total += REFERRAL_COMMISSIONS.level2;
                referralProfits.push({
                  id: `ref-l2-${level2.username || level2.phone}`,
                  label: `Parrainage Niveau 2 — ${level2.name}`,
                  amount: REFERRAL_COMMISSIONS.level2,
                  date: level2.activatedAt
                    ? new Date(level2.activatedAt).toLocaleDateString('fr-FR')
                    : new Date().toLocaleDateString('fr-FR'),
                  type: 'parrainage',
                  level: 2,
                });
              }

              // Niveau 3
              const level2Id = level2.username || level2.referralCode || level2.name;
              if (level2Id) {
                const level3Users = await getFilleulsFor(level2Id, level2.name);
                for (const level3 of level3Users) {
                  const l3Activated = level3.isActivated || level3.activated === true || level3.accountStatus === 'active';
                  if (l3Activated) {
                    l3Total += REFERRAL_COMMISSIONS.level3;
                    referralProfits.push({
                      id: `ref-l3-${level3.username || level3.phone}`,
                      label: `Parrainage Niveau 3 — ${level3.name}`,
                      amount: REFERRAL_COMMISSIONS.level3,
                      date: level3.activatedAt
                        ? new Date(level3.activatedAt).toLocaleDateString('fr-FR')
                        : new Date().toLocaleDateString('fr-FR'),
                      type: 'parrainage',
                      level: 3,
                    });
                  }
                }
              }
            }
          }
        }
      }

      setCommissionStats({ level1: l1Total, level2: l2Total, level3: l3Total, total: l1Total + l2Total + l3Total });

      // --- PROFITS AUTRES (tâches, roue, etc.) ---
      const rawProfits: Array<{ amount: number; type: string; date: string; phone?: string }> =
        lsGet('userProfits', []);

      const otherProfits: Profit[] = rawProfits
        .filter(p => !p.phone || p.phone === userPhone)
        .filter(p => {
          const t = (p.type || '').toLowerCase();
          return !t.includes('parrainage');
        })
        .map((p, idx) => {
          const t = (p.type || '').toLowerCase();
          let profitType: Profit['type'] = 'autre';
          if (t.includes('tâche') || t.includes('tache')) profitType = 'tache';
          else if (t.includes('roue') || t.includes('chance')) profitType = 'roue';
          return {
            id: `other-${idx}-${p.date}`,
            label: p.type || 'Gain divers',
            amount: Number(p.amount) || 0,
            date: p.date || new Date().toLocaleDateString('fr-FR'),
            type: profitType,
          };
        });

      setProfits([...referralProfits, ...otherProfits]);
    } catch (error) {
      console.error('Erreur chargement revenus:', error);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const totalProfits = profits.reduce((sum, p) => sum + p.amount, 0);
  const balance = totalProfits - totalExpenses;

  const profitTypeColor = (type: string, level?: number) => {
    if (type === 'parrainage') {
      if (level === 1) return 'bg-emerald-100 text-emerald-700';
      if (level === 2) return 'bg-teal-100 text-teal-700';
      return 'bg-cyan-100 text-cyan-700';
    }
    if (type === 'tache') return 'bg-amber-100 text-amber-700';
    if (type === 'roue') return 'bg-orange-100 text-orange-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      {isLoading && (
        <div className="fixed top-4 right-4 z-50 bg-teal-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin"></i>
          <span className="text-sm font-semibold">Chargement des revenus...</span>
        </div>
      )}

      {/* Résumé */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-red-400">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-lg">
              <i className="ri-arrow-down-circle-line text-xl text-red-500"></i>
            </div>
            <span className="text-sm font-semibold text-gray-500">Dépenses</span>
          </div>
          <p className="text-2xl font-extrabold text-red-600">{totalExpenses.toLocaleString()}</p>
          <p className="text-xs text-gray-400 mt-1">{currency}</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-green-400">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg">
              <i className="ri-arrow-up-circle-line text-xl text-green-500"></i>
            </div>
            <span className="text-sm font-semibold text-gray-500">Profits calculés</span>
          </div>
          <p className="text-2xl font-extrabold text-green-600">{totalProfits.toLocaleString()}</p>
          <p className="text-xs text-gray-400 mt-1">{currency}</p>
        </div>

        <div className={`bg-white rounded-xl shadow-lg p-5 border-l-4 ${balance >= 0 ? 'border-teal-400' : 'border-orange-400'}`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${balance >= 0 ? 'bg-teal-100' : 'bg-orange-100'}`}>
              <i className={`ri-scales-line text-xl ${balance >= 0 ? 'text-teal-500' : 'text-orange-500'}`}></i>
            </div>
            <span className="text-sm font-semibold text-gray-500">Solde net</span>
          </div>
          <p className={`text-2xl font-extrabold ${balance >= 0 ? 'text-teal-600' : 'text-orange-600'}`}>
            {balance >= 0 ? '+' : ''}{balance.toLocaleString()}
          </p>
          <p className="text-xs text-gray-400 mt-1">{currency}</p>
        </div>
      </div>

      {/* Solde disponible */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl shadow-lg p-5 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold opacity-90 mb-1">💰 Solde disponible (crédité)</p>
            <p className="text-3xl font-extrabold">{realBalance.toLocaleString()} {currency}</p>
            <p className="text-xs opacity-75 mt-1">Commissions créditées automatiquement à chaque activation de filleul</p>
          </div>
          <button
            onClick={() => loadRevenueData()}
            disabled={isLoading}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-50"
          >
            <i className={`ri-refresh-line ${isLoading ? 'animate-spin' : ''}`}></i>
            Actualiser
          </button>
        </div>
      </div>

      {/* Commissions par niveau */}
      <div className="bg-white rounded-xl shadow-lg p-5">
        <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
          <i className="ri-team-line text-teal-500"></i> Commissions par niveau
        </h3>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {([
            { label: 'Niveau 1', value: commissionStats.level1, rate: REFERRAL_COMMISSIONS.level1, color: 'emerald' },
            { label: 'Niveau 2', value: commissionStats.level2, rate: REFERRAL_COMMISSIONS.level2, color: 'teal' },
            { label: 'Niveau 3', value: commissionStats.level3, rate: REFERRAL_COMMISSIONS.level3, color: 'cyan' },
          ] as const).map(({ label, value, rate, color }) => (
            <div key={label} className={`bg-${color}-50 rounded-lg p-3 text-center border border-${color}-200`}>
              <p className={`text-xs text-${color}-600 font-semibold mb-1`}>{label}</p>
              <p className={`text-xl font-extrabold text-${color}-700`}>{value.toLocaleString()}</p>
              <p className={`text-xs text-${color}-500`}>{currency} gagné</p>
              <p className={`text-xs text-${color}-400 mt-1`}>{rate} F / filleul</p>
            </div>
          ))}
        </div>
        <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg p-3 flex items-center justify-between border border-teal-200">
          <span className="text-sm font-bold text-teal-800">Total commissions parrainage</span>
          <span className="text-lg font-extrabold text-teal-700">{commissionStats.total.toLocaleString()} {currency}</span>
        </div>
      </div>

      {/* Onglets */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('depenses')}
            className={`flex-1 py-3 text-sm font-semibold transition cursor-pointer whitespace-nowrap ${activeTab === 'depenses' ? 'bg-red-50 text-red-600 border-b-2 border-red-500' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <i className="ri-arrow-down-circle-line mr-1"></i> Dépenses
          </button>
          <button
            onClick={() => setActiveTab('profits')}
            className={`flex-1 py-3 text-sm font-semibold transition cursor-pointer whitespace-nowrap ${activeTab === 'profits' ? 'bg-green-50 text-green-600 border-b-2 border-green-500' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <i className="ri-arrow-up-circle-line mr-1"></i> Profits ({profits.length})
          </button>
        </div>

        {activeTab === 'depenses' && (
          expenses.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-5xl text-gray-300 mb-3"></i>
              <p className="text-gray-500 font-semibold">Aucune dépense enregistrée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-red-600 uppercase">Description</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-red-600 uppercase">Date</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-red-600 uppercase">Montant</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {expenses.map((exp) => (
                    <tr key={exp.id} className="hover:bg-red-50/30">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${exp.type === 'activation' ? 'bg-red-500' : 'bg-gray-400'}`}></span>
                          <span className="text-sm text-gray-700">{exp.label}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-500">{exp.date}</td>
                      <td className="px-5 py-4 text-sm font-bold text-red-600 text-right">-{exp.amount.toLocaleString()} {currency}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-red-50">
                  <tr>
                    <td colSpan={2} className="px-5 py-3 text-sm font-bold text-red-700">Total dépenses</td>
                    <td className="px-5 py-3 text-sm font-extrabold text-red-700 text-right">-{totalExpenses.toLocaleString()} {currency}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )
        )}

        {activeTab === 'profits' && (
          profits.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-money-dollar-circle-line text-5xl text-gray-300 mb-3"></i>
              <p className="text-gray-500 font-semibold">Aucun profit pour le moment</p>
              <p className="text-xs text-gray-400 mt-1">Parrainez des amis et accomplissez des tâches pour gagner</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-green-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-green-600 uppercase">Description</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-green-600 uppercase">Type</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-green-600 uppercase">Date</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-green-600 uppercase">Montant</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {profits.map((profit) => (
                    <tr key={profit.id} className="hover:bg-green-50/30">
                      <td className="px-5 py-4 text-sm text-gray-700">{profit.label}</td>
                      <td className="px-5 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${profitTypeColor(profit.type, profit.level)}`}>
                          {profit.type === 'parrainage' ? `Parrainage N${profit.level}` :
                           profit.type === 'tache' ? 'Tâche' :
                           profit.type === 'roue' ? 'Roue' : 'Autre'}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-500">{profit.date}</td>
                      <td className="px-5 py-4 text-sm font-bold text-green-600 text-right">+{profit.amount.toLocaleString()} {currency}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-green-50">
                  <tr>
                    <td colSpan={3} className="px-5 py-3 text-sm font-bold text-green-700">Total profits</td>
                    <td className="px-5 py-3 text-sm font-extrabold text-green-700 text-right">+{totalProfits.toLocaleString()} {currency}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}
